import React, { useRef, useState, useEffect } from "react";
import { Button, Card, Table, Tag, Progress, Tooltip, Typography, Divider, Modal, message, Checkbox, Input, Space, Select, Spin, Alert } from "antd";
import {
  CheckCircleOutlined,
  RocketOutlined,
  TeamOutlined,
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  FilePdfOutlined,
  PlusOutlined,
  EyeOutlined,
  FolderAddOutlined,
  BulbOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Text, Paragraph } = Typography;

const ReportWrapper = styled.div`
  background: linear-gradient(180deg, #f8fafb 0%, #f0f4f7 100%);
  min-height: 100vh;
  
  .report-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 24px;
  }

  .report-header {
    background: linear-gradient(135deg, #014c75 0%, #0a6d9c 50%, #01d9a9 100%);
    border-radius: 20px;
    padding: 40px 48px;
    color: white;
    margin-bottom: 32px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(1, 76, 117, 0.25);
    
    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -20%;
      width: 60%;
      height: 200%;
      background: radial-gradient(ellipse, rgba(1, 217, 169, 0.15) 0%, transparent 70%);
      pointer-events: none;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #01d9a9 0%, #00f5c4 50%, #01d9a9 100%);
    }
    
    .header-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
    }
    
    .header-badge {
      background: rgba(1, 217, 169, 0.25);
      padding: 6px 16px;
      border-radius: 24px;
      font-size: 12px;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border: 1px solid rgba(1, 217, 169, 0.4);
      
      &::before {
        content: '⚡';
      }
    }
    
    .enterprise-tag {
      background: rgba(255, 255, 255, 0.15);
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
    }
    
    h1 {
      color: white;
      font-size: 32px;
      font-weight: 700;
      margin-bottom: 12px;
      letter-spacing: -0.5px;
    }
    
    .header-subtitle {
      opacity: 0.9;
      font-size: 16px;
      margin-bottom: 8px;
    }
    
    .header-meta {
      display: flex;
      gap: 24px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.15);
      
      .meta-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        opacity: 0.9;
        
        .meta-icon {
          width: 28px;
          height: 28px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }
      }
    }
  }

  .section-card {
    margin-bottom: 24px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
    border: 1px solid rgba(1, 76, 117, 0.06);
    page-break-inside: avoid;
    break-inside: avoid;
    transition: box-shadow 0.3s ease, transform 0.2s ease;
    
    &:hover {
      box-shadow: 0 8px 32px rgba(0,0,0,0.08);
    }
    
    .ant-card-head {
      border-bottom: 1px solid #e8eef3;
      min-height: 64px;
      background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
      border-radius: 16px 16px 0 0;
      
      .ant-card-head-title {
        display: flex;
        align-items: center;
        gap: 14px;
        font-weight: 700;
        font-size: 16px;
        padding: 18px 0;
        color: #014c75;
        
        .section-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
      }
    }
    
    .ant-card-body {
      padding: 28px;
    }
  }
  
  .data-credibility-footer {
    background: linear-gradient(135deg, #014c75 0%, #0a6d9c 100%);
    border-radius: 16px;
    padding: 24px 32px;
    margin-top: 32px;
    display: flex;
    align-items: center;
    gap: 20px;
    color: white;
    box-shadow: 0 8px 32px rgba(1, 76, 117, 0.2);
    
    .credibility-icon {
      width: 56px;
      height: 56px;
      background: rgba(1, 217, 169, 0.2);
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 26px;
      flex-shrink: 0;
      border: 1px solid rgba(1, 217, 169, 0.3);
    }
    
    .credibility-content {
      flex: 1;
      
      .credibility-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 6px;
        display: flex;
        align-items: center;
        gap: 8px;
        
        .verified-badge {
          background: #01d9a9;
          color: #014c75;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
      
      .credibility-text {
        font-size: 13px;
        opacity: 0.9;
        line-height: 1.5;
      }
      
      .source-logos {
        display: flex;
        gap: 16px;
        margin-top: 12px;
        flex-wrap: wrap;
        
        .source-item {
          background: rgba(255, 255, 255, 0.1);
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          border: 1px solid rgba(255, 255, 255, 0.15);
        }
      }
    }
  }
  
  .hide-in-pdf {
    display: block;
  }
  
  &.pdf-mode {
    background: white !important;
    
    .report-container {
      padding: 20px !important;
    }
    
    .hide-in-pdf {
      display: none !important;
    }
    
    .report-header {
      border-radius: 12px !important;
      padding: 28px 32px !important;
      margin-bottom: 20px !important;
      box-shadow: none !important;
      
      &::before,
      &::after {
        display: none !important;
      }
      
      button {
        display: none !important;
      }
      
      h1 {
        font-size: 26px !important;
        margin-bottom: 8px !important;
      }
      
      .header-top {
        margin-bottom: 12px !important;
      }
      
      .header-meta {
        margin-top: 12px !important;
        padding-top: 12px !important;
        gap: 16px !important;
        flex-wrap: wrap !important;
        
        .meta-item {
          font-size: 12px !important;
          
          .meta-icon {
            width: 22px !important;
            height: 22px !important;
            font-size: 11px !important;
          }
        }
      }
    }
    
    .section-card {
      border-radius: 10px !important;
      box-shadow: 0 1px 4px rgba(0,0,0,0.08) !important;
      margin-bottom: 16px !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      
      .ant-card-head {
        min-height: 48px !important;
        padding: 0 20px !important;
        
        .ant-card-head-title {
          font-size: 14px !important;
          padding: 12px 0 !important;
          
          .section-icon {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            font-size: 14px !important;
          }
        }
      }
      
      .ant-card-body {
        padding: 20px !important;
      }
    }
    
    .action-card {
      display: none !important;
    }
    
    .report-actions {
      display: none !important;
    }
    
    .data-credibility-footer {
      border-radius: 10px !important;
      padding: 16px 24px !important;
      margin-top: 16px !important;
      box-shadow: none !important;
      page-break-inside: avoid !important;
      
      .credibility-icon {
        width: 40px !important;
        height: 40px !important;
        font-size: 20px !important;
      }
      
      .credibility-content {
        .credibility-title {
          font-size: 12px !important;
        }
        
        .credibility-text {
          font-size: 11px !important;
        }
        
        .source-logos {
          margin-top: 8px !important;
          gap: 10px !important;
          
          .source-item {
            font-size: 10px !important;
            padding: 2px 8px !important;
          }
        }
      }
    }
    
    .ant-table {
      font-size: 12px !important;
      
      .ant-table-thead > tr > th {
        padding: 8px 12px !important;
        font-size: 11px !important;
      }
      
      .ant-table-tbody > tr > td {
        padding: 8px 12px !important;
      }
    }
    
    .job-card {
      padding: 16px !important;
      margin-bottom: 12px !important;
      page-break-inside: avoid !important;
      
      .job-header .job-title {
        font-size: 15px !important;
      }
    }
    
    .roadmap-timeline .phase-item {
      padding-bottom: 16px !important;
      page-break-inside: avoid !important;
    }
    
    .maturity-display {
      .maturity-score .score-value {
        font-size: 36px !important;
      }
    }
    
    .risk-grid {
      gap: 12px !important;
      
      .risk-item {
        padding: 12px !important;
      }
    }
    
    .team-section {
      margin-bottom: 16px !important;
    }
    
    .savings-highlight {
      padding: 16px !important;
      
      .savings-icon {
        width: 40px !important;
        height: 40px !important;
        font-size: 18px !important;
      }
      
      .savings-text .savings-percentage {
        font-size: 20px !important;
      }
    }
    
    .next-actions-card {
      page-break-inside: avoid !important;
      
      .actions-list {
        .action-item {
          padding: 12px 16px !important;
          margin-bottom: 8px !important;
          
          .action-number {
            width: 24px !important;
            height: 24px !important;
            font-size: 12px !important;
          }
          
          .action-text {
            font-size: 13px !important;
          }
          
          .action-arrow {
            display: none !important;
          }
        }
      }
    }
    
    .budget-overview {
      padding: 16px !important;
      page-break-inside: avoid !important;
    }
    
    .summary-grid {
      gap: 12px !important;
      
      .summary-item {
        padding: 12px !important;
        
        .summary-value {
          font-size: 20px !important;
        }
      }
    }
  }

  .maturity-display {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    
    .maturity-score {
      text-align: center;
      
      .score-value {
        font-size: 48px;
        font-weight: 700;
        color: #014c75;
        line-height: 1;
      }
      
      .score-label {
        color: #8c8c8c;
        font-size: 12px;
        margin-top: 4px;
      }
    }
    
    .maturity-details {
      flex: 1;
      
      .stage-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 20px;
        font-weight: 600;
        margin-bottom: 8px;
        
        &.foundational { background: #fff1f0; color: #cf1322; }
        &.emerging { background: #fff7e6; color: #d46b08; }
        &.accelerating { background: #e6f7ff; color: #1890ff; }
        &.transformative { background: #f6ffed; color: #52c41a; }
      }
    }
  }

  .risk-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    .risk-item {
      padding: 16px;
      background: #fafafa;
      border-radius: 8px;
      text-align: center;
      
      .risk-label {
        color: #8c8c8c;
        font-size: 12px;
        margin-bottom: 8px;
      }
      
      .risk-value {
        font-size: 18px;
        font-weight: 600;
        
        &.low { color: #52c41a; }
        &.medium { color: #faad14; }
        &.high { color: #f5222d; }
        &.very-high { color: #cf1322; }
      }
    }
  }

  .risk-factors-list {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      .risk-bullet {
        color: #faad14;
        margin-top: 4px;
      }
    }
  }

  .team-section {
    margin-bottom: 24px;
    
    .team-title {
      font-weight: 600;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .team-total {
      text-align: right;
      padding: 12px;
      background: #e6f7ff;
      border-radius: 8px;
      margin-top: 12px;
      
      .total-label {
        color: #8c8c8c;
        font-size: 12px;
      }
      
      .total-value {
        font-size: 20px;
        font-weight: 700;
        color: #014c75;
      }
    }
  }

  .cost-comparison {
    margin-bottom: 24px;
  }

  .savings-highlight {
    background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    
    .savings-icon {
      width: 48px;
      height: 48px;
      background: #52c41a;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 24px;
    }
    
    .savings-text {
      flex: 1;
      
      .savings-percentage {
        font-size: 24px;
        font-weight: 700;
        color: #52c41a;
      }
      
      .savings-desc {
        color: #595959;
      }
    }
  }

  .roadmap-timeline {
    position: relative;
    padding-left: 30px;
    
    &::before {
      content: '';
      position: absolute;
      left: 10px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #e8e8e8;
    }
    
    .phase-item {
      position: relative;
      padding-bottom: 24px;
      
      &:last-child {
        padding-bottom: 0;
      }
      
      .phase-dot {
        position: absolute;
        left: -26px;
        top: 4px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #014c75;
        border: 3px solid #e6f7ff;
      }
      
      .phase-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;
        
        .phase-name {
          font-weight: 600;
          color: #014c75;
        }
        
        .phase-timeline {
          background: #f0f0f0;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          color: #595959;
        }
      }
      
      .phase-roles {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
      }
      
      .phase-desc {
        color: #8c8c8c;
        font-size: 13px;
      }
    }
  }

  .job-card {
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    transition: box-shadow 0.3s;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }
    
    .job-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
      
      .job-title {
        font-size: 18px;
        font-weight: 600;
        color: #014c75;
      }
      
      .job-budget {
        text-align: right;
        
        .budget-value {
          font-size: 18px;
          font-weight: 600;
          color: #52c41a;
        }
        
        .budget-label {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }
    
    .job-why {
      background: #f6f8fa;
      padding: 12px;
      border-radius: 8px;
      margin-bottom: 12px;
      font-size: 14px;
      color: #595959;
      border-left: 3px solid #014c75;
    }
    
    .job-skills {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
      
      .skill-tag {
        &.primary {
          background: #e6f7ff;
          color: #1890ff;
          border-color: #91d5ff;
        }
        
        &.secondary {
          background: #f5f5f5;
          color: #595959;
        }
      }
    }
    
    .job-meta {
      display: flex;
      gap: 24px;
      color: #8c8c8c;
      font-size: 13px;
      
      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }

  .actions-list {
    .action-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      background: #fafafa;
      border-radius: 8px;
      margin-bottom: 8px;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        background: #e6f7ff;
        transform: translateX(4px);
      }
      
      .action-number {
        width: 24px;
        height: 24px;
        background: #014c75;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;
      }
      
      .action-text {
        flex: 1;
        font-weight: 500;
      }
      
      .action-arrow {
        color: #014c75;
      }
    }
  }

  .report-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;
  }

  .action-card {
    .action-buttons-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 16px;
    }

    .action-button-card {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px;
      border: 1px solid #f0f0f0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        border-color: #01d9a9;
        box-shadow: 0 4px 12px rgba(1, 217, 169, 0.15);
        transform: translateY(-2px);
      }

      .action-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        flex-shrink: 0;
      }

      .action-content {
        flex: 1;

        .action-title {
          font-weight: 600;
          font-size: 16px;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .action-description {
          font-size: 13px;
          color: #8c8c8c;
          line-height: 1.4;
        }
      }

      .action-arrow-icon {
        color: #bfbfbf;
        font-size: 16px;
        transition: color 0.3s ease;
      }

      &:hover .action-arrow-icon {
        color: #01d9a9;
      }
    }
  }
`;

const formatCurrency = (amount) => {
  if (!amount) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const getMaturityStageClass = (stage) => {
  const stageMap = {
    Foundational: "foundational",
    Emerging: "emerging",
    Accelerating: "accelerating",
    Transformative: "transformative",
  };
  return stageMap[stage] || "emerging";
};

const getRiskClass = (risk) => {
  const riskMap = {
    Low: "low",
    Medium: "medium",
    High: "high",
    "Very High": "very-high",
  };
  return riskMap[risk] || "medium";
};

const AdminAssessmentReportDisplay = ({ assessment, onBack, onCreateJob, onViewAssessments, onNavigate }) => {
  const report = assessment?.assessmentReport;
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [creatingJobs, setCreatingJobs] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [activeEngagements, setActiveEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [loadingEngagements, setLoadingEngagements] = useState(false);

  useEffect(() => {
    if (actionModalVisible && (actionType === "project" || actionType === "jobs")) {
      fetchActiveEngagements();
    }
  }, [actionModalVisible, actionType]);

  const fetchActiveEngagements = async () => {
    setLoadingEngagements(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ACTIVE_ENGAGEMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveEngagements(response.data.engagements || []);
    } catch (error) {
      console.error("Error fetching engagements:", error);
    } finally {
      setLoadingEngagements(false);
    }
  };
  
  const safeNavigate = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      window.location.href = path;
    }
  };

  const handleOpenActionModal = (type) => {
    setActionType(type);
    setSelectedJobs([]);
    setProjectName(assessment?.requirementDescription?.substring(0, 50) || "New Project");
    setActionModalVisible(true);
  };

  const handleCreateDraftJobs = async () => {
    if (selectedJobs.length === 0) {
      message.warning("Please select at least one job to create");
      return;
    }

    if (!selectedEngagement) {
      message.warning("Please select an engagement first");
      return;
    }

    setCreatingJobs(true);
    try {
      const token = localStorage.getItem("authToken");
      const jobs = report?.jobDescriptions || [];
      
      for (const jobIndex of selectedJobs) {
        const job = jobs[jobIndex];
        if (!job) continue;

        const monthlyBudget = job.monthlyBudgetINR || 100000;
        const durationMonths = job.recommendedDurationMonths || 6;

        const jobData = {
          engagementId: selectedEngagement,
          role: job.role,
          jobResponsibilities: job.jobResponsibilities || job.whyRequired || "",
          primarySkills: job.primarySkills || [],
          secondarySkills: job.secondarySkills || [],
          experienceRange: job.experienceRange || "3-5 years",
          engagementType: "Full-time",
          reportingLocation: "Remote",
          worldwide: false,
          budget: monthlyBudget,
          budgetPeriod: "Per/month",
          monthsOfEngagement: `${durationMonths} months`,
          numberOfRequirements: 1,
          numberOfRequirementsPeriod: "Per/month",
          communication: "Excellent",
          workingTime: "Flexible",
          workingHoursPerWeek: 40,
          travelPreference: "No Travel",
          provideSystem: "No",
          toolsUsed: [],
          shiftTiming: "Flexible",
          status: "draft",
        };

        await axios.post(
          API_CONST.ADD_JOB_REQUIREMENTS,
          jobData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      message.success(`${selectedJobs.length} draft job(s) created successfully!`);
      setActionModalVisible(false);
      safeNavigate("/admin/jobs");
    } catch (error) {
      console.error("Error creating jobs:", error);
      message.error(error.response?.data?.error || "Failed to create jobs");
    } finally {
      setCreatingJobs(false);
    }
  };

  const mapDeliveryTypeFromSurvey = (projectType) => {
    const mapping = {
      'new_product': 'build',
      'modernizing': 'enhance',
      'integration': 'enhance',
      'migration': 'migrate',
      'continuous_improvement': 'enhance'
    };
    return mapping[projectType] || 'build';
  };

  const mapComplexityFromRisk = (risk) => {
    const mapping = {
      'Low': 25,
      'Medium': 50,
      'High': 75,
      'Very High': 90
    };
    return mapping[risk] || 50;
  };

  const mapArchitectureFromSurvey = (solutionFocus) => {
    const mapping = {
      'web_app': 'monolith',
      'mobile_app': 'monolith',
      'data_analytics': 'hybrid',
      'cloud_devops': 'microservices',
      'ai_automation': 'hybrid'
    };
    return mapping[solutionFocus] || 'monolith';
  };

  const extractCriticalRoles = () => {
    const jobs = report?.jobDescriptions || [];
    return jobs.map(job => job.role).slice(0, 5);
  };

  const calculateExpectedTeamSize = () => {
    const impactTeam = report?.resourceStrategy?.impactTeam || [];
    return impactTeam.reduce((sum, member) => sum + (member.quantity || 0), 0);
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      message.warning("Please enter a project name");
      return;
    }

    if (!selectedEngagement) {
      message.warning("Please select an engagement");
      return;
    }

    setCreatingProject(true);
    try {
      const token = localStorage.getItem("authToken");
      const surveyResponses = assessment?.surveyResponses || {};
      
      // Split technologies: top 3 to primary, rest to secondary
      const allTechnologies = assessment?.technologies || [];
      const primaryTech = allTechnologies.slice(0, 3);
      const secondaryTech = allTechnologies.slice(3);

      const projectData = {
        engagementId: selectedEngagement,
        title: projectName,
        description: assessment?.requirementDescription || "",
        projectObjective: assessment?.requirementDescription || "",
        creationMode: "AI_ASSESSMENT",
        deliveryType: mapDeliveryTypeFromSurvey(surveyResponses.projectType),
        primaryTechnologies: primaryTech,
        secondaryTechnologies: secondaryTech,
        architectureType: mapArchitectureFromSurvey(surveyResponses.solutionFocus),
        integrationComplexity: surveyResponses.technicalMaturity === 'cloud_native' ? 'high' : 
                              surveyResponses.technicalMaturity === 'moderate' ? 'medium' : 'low',
        complexityLevel: mapComplexityFromRisk(report?.digitalReadiness?.technicalComplexity),
        maturityIndex: report?.executiveSummary?.transformationMaturityIndex || 3,
        dataSensitivity: surveyResponses.dataSensitivity === 'high' || surveyResponses.dataSensitivity === 'regulated',
        expectedTeamSize: calculateExpectedTeamSize(),
        criticalRoles: extractCriticalRoles(),
        budgetAllocated: report?.resourceStrategy?.totalImpactTeamCost || 0,
        expectedStartDate: new Date().toISOString().split("T")[0],
      };

      const response = await axios.post(
        API_CONST.CREATE_PROJECT,
        projectData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const projectId = response.data.project?.id;
        message.success("Project created successfully with all assessment data mapped!");
        setActionModalVisible(false);
        safeNavigate(`/admin/projects/details/${projectId}`);
      } else {
        message.error(response.data.error || "Failed to create project");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      message.error(error.response?.data?.error || "Failed to create project. Please ensure you have an active engagement.");
    } finally {
      setCreatingProject(false);
    }
  };

  const renderActionModal = () => {
    const jobs = report?.jobDescriptions || [];

    const modalStyles = {
      header: {
        background: "linear-gradient(135deg, #014c75 0%, #0277a8 100%)",
        padding: "24px 32px",
        margin: "-24px -24px 24px -24px",
        borderRadius: "8px 8px 0 0",
        color: "#fff",
      },
      headerTitle: {
        fontSize: 22,
        fontWeight: 600,
        color: "#fff",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: 12,
      },
      headerSubtitle: {
        fontSize: 14,
        color: "rgba(255,255,255,0.85)",
        marginTop: 8,
      },
      twoColumn: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 20,
      },
      formGroup: {
        marginBottom: 20,
      },
      label: {
        display: "block",
        marginBottom: 8,
        fontWeight: 500,
        color: "#333",
        fontSize: 14,
      },
      mappedSection: {
        background: "linear-gradient(180deg, #f8fafc 0%, #fff 100%)",
        border: "1px solid #e8eef3",
        borderRadius: 12,
        padding: 24,
        marginTop: 24,
      },
      mappedHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 20,
        paddingBottom: 16,
        borderBottom: "1px solid #e8eef3",
      },
      metricCard: {
        background: "#fff",
        border: "1px solid #e8eef3",
        borderRadius: 10,
        padding: 16,
        textAlign: "center",
        transition: "box-shadow 0.2s",
      },
      metricValue: {
        fontSize: 20,
        fontWeight: 600,
        color: "#014c75",
        marginBottom: 4,
      },
      metricLabel: {
        fontSize: 12,
        color: "#8c8c8c",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      footer: {
        marginTop: 32,
        paddingTop: 20,
        borderTop: "1px solid #e8eef3",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      },
    };

    return (
      <Modal
        open={actionModalVisible}
        onCancel={() => setActionModalVisible(false)}
        footer={null}
        width={960}
        bodyStyle={{ padding: 24 }}
        closable={true}
        className="assessment-action-modal"
      >
        {actionType === "project" ? (
          <div>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.headerTitle}>
                <FolderAddOutlined /> Create Project from Assessment
              </h2>
              <p style={modalStyles.headerSubtitle}>
                Transform your AI assessment into a fully configured project with team recommendations and budget planning
              </p>
            </div>

            {activeEngagements.length === 0 && !loadingEngagements && (
              <Alert
                message="No Active Engagements"
                description="You need to create an engagement first before creating a project. Projects must belong to an engagement."
                type="warning"
                showIcon
                style={{ marginBottom: 24, borderRadius: 8 }}
                action={
                  <Button size="small" onClick={() => {
                    setActionModalVisible(false);
                    safeNavigate("/admin/engagements/create");
                  }}>
                    Create Engagement
                  </Button>
                }
              />
            )}
            
            <div style={modalStyles.twoColumn}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Select Engagement <span style={{ color: "#ff4d4f" }}>*</span></label>
                {loadingEngagements ? (
                  <Spin size="small" />
                ) : (
                  <Select
                    placeholder="Choose an active engagement"
                    style={{ width: "100%" }}
                    size="large"
                    value={selectedEngagement}
                    onChange={setSelectedEngagement}
                    disabled={activeEngagements.length === 0}
                  >
                    {activeEngagements.map(eng => (
                      <Select.Option key={eng.id} value={eng.id}>
                        {eng.title}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </div>

              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Project Name <span style={{ color: "#ff4d4f" }}>*</span></label>
                <Input
                  size="large"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                />
              </div>
            </div>

            <div style={modalStyles.mappedSection}>
              <div style={modalStyles.mappedHeader}>
                <ThunderboltOutlined style={{ fontSize: 18, color: "#01d9a9" }} />
                <Text strong style={{ fontSize: 16 }}>Auto-Mapped from Assessment</Text>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>Requirement Description</Text>
                <div style={{ marginTop: 8, padding: 12, background: "#fff", borderRadius: 8, border: "1px solid #e8eef3" }}>
                  <Text>{(assessment?.requirementDescription || "").substring(0, 250)}{(assessment?.requirementDescription || "").length > 250 ? "..." : ""}</Text>
                </div>
              </div>
              
              <div style={{ marginBottom: 20 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>Primary Technologies</Text>
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(assessment?.technologies || []).map((tech, i) => (
                    <Tag key={i} color="blue" style={{ padding: "4px 12px", fontSize: 13 }}>{tech}</Tag>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
                <div style={modalStyles.metricCard}>
                  <div style={modalStyles.metricValue}>{mapDeliveryTypeFromSurvey(assessment?.surveyResponses?.projectType)}</div>
                  <div style={modalStyles.metricLabel}>Delivery Type</div>
                </div>
                <div style={modalStyles.metricCard}>
                  <div style={modalStyles.metricValue}>{mapArchitectureFromSurvey(assessment?.surveyResponses?.solutionFocus)}</div>
                  <div style={modalStyles.metricLabel}>Architecture</div>
                </div>
                <div style={modalStyles.metricCard}>
                  <div style={{ ...modalStyles.metricValue, color: "#52c41a" }}>{calculateExpectedTeamSize()}</div>
                  <div style={modalStyles.metricLabel}>Team Size</div>
                </div>
                <div style={modalStyles.metricCard}>
                  <div style={{ ...modalStyles.metricValue, color: "#fa8c16" }}>{report?.executiveSummary?.transformationMaturityIndex || 50}/100</div>
                  <div style={modalStyles.metricLabel}>Maturity Index</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 13 }}>Critical Roles</Text>
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {extractCriticalRoles().map((role, i) => (
                      <Tag key={i} color="purple" style={{ padding: "4px 12px", fontSize: 13 }}>{role}</Tag>
                    ))}
                  </div>
                </div>
                <div style={{ ...modalStyles.metricCard, background: "linear-gradient(135deg, #014c75 0%, #0277a8 100%)" }}>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>Estimated Budget</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#fff" }}>
                    {formatCurrency(report?.resourceStrategy?.totalImpactTeamCost || 0)}
                  </div>
                </div>
              </div>
            </div>

            <div style={modalStyles.footer}>
              <Text type="secondary">All fields will be auto-populated from your assessment</Text>
              <Space size="middle">
                <Button size="large" onClick={() => setActionModalVisible(false)}>Cancel</Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<FolderAddOutlined />}
                  loading={creatingProject}
                  onClick={handleCreateProject}
                  disabled={activeEngagements.length === 0 || !selectedEngagement || !projectName}
                  style={{ background: "#01d9a9", borderColor: "#01d9a9", height: 44, paddingLeft: 24, paddingRight: 24 }}
                >
                  Create Project
                </Button>
              </Space>
            </div>
          </div>
        ) : (
          <div>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.headerTitle}>
                <PlusOutlined /> Create Jobs from Recommendations
              </h2>
              <p style={modalStyles.headerSubtitle}>
                Select AI-recommended roles to create draft job postings for your hiring needs
              </p>
            </div>

            {activeEngagements.length === 0 && !loadingEngagements && (
              <Alert
                message="No Active Engagements"
                description="You need to create an engagement first before creating jobs. Jobs must belong to an engagement."
                type="warning"
                showIcon
                style={{ marginBottom: 24, borderRadius: 8 }}
                action={
                  <Button size="small" onClick={() => {
                    setActionModalVisible(false);
                    safeNavigate("/admin/engagements/create");
                  }}>
                    Create Engagement
                  </Button>
                }
              />
            )}
            
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>Select Engagement <span style={{ color: "#ff4d4f" }}>*</span></label>
              {loadingEngagements ? (
                <Spin size="small" />
              ) : (
                <Select
                  placeholder="Choose an active engagement"
                  style={{ width: "100%" }}
                  size="large"
                  value={selectedEngagement}
                  onChange={setSelectedEngagement}
                  disabled={activeEngagements.length === 0}
                >
                  {activeEngagements.map(eng => (
                    <Select.Option key={eng.id} value={eng.id}>
                      {eng.title}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>

            <div style={modalStyles.mappedSection}>
              <div style={modalStyles.mappedHeader}>
                <TeamOutlined style={{ fontSize: 18, color: "#1890ff" }} />
                <Text strong style={{ fontSize: 16 }}>Recommended Job Roles</Text>
                <Tag color="blue" style={{ marginLeft: "auto" }}>{jobs.length} roles available</Tag>
              </div>

              {jobs.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxHeight: 380, overflowY: "auto", paddingRight: 8 }}>
                  {jobs.map((job, index) => (
                    <Card
                      key={index}
                      size="small"
                      hoverable
                      style={{ 
                        border: selectedJobs.includes(index) ? "2px solid #01d9a9" : "1px solid #e8eef3",
                        borderRadius: 12,
                        cursor: "pointer",
                        background: selectedJobs.includes(index) ? "rgba(1, 217, 169, 0.05)" : "#fff",
                        transition: "all 0.2s ease"
                      }}
                      bodyStyle={{ padding: 16 }}
                      onClick={() => {
                        setSelectedJobs(prev => 
                          prev.includes(index) 
                            ? prev.filter(i => i !== index) 
                            : [...prev, index]
                        );
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <Checkbox 
                          checked={selectedJobs.includes(index)} 
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: "#014c75" }}>{job.role}</div>
                          <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                            <Tag color="geekblue" style={{ margin: 0 }}>{job.experienceRange}</Tag>
                            <Tag color="cyan" style={{ margin: 0 }}>{job.recommendedDurationMonths} months</Tag>
                          </div>
                          {(job.jobResponsibilities || job.whyRequired) && (
                            <div style={{ fontSize: 13, color: "#666", marginBottom: 10, lineHeight: 1.5 }}>
                              {(job.jobResponsibilities || job.whyRequired).length > 100 
                                ? `${(job.jobResponsibilities || job.whyRequired).substring(0, 100)}...`
                                : (job.jobResponsibilities || job.whyRequired)}
                            </div>
                          )}
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {(job.primarySkills || []).slice(0, 3).map((skill, i) => (
                              <Tag key={i} color="blue" style={{ fontSize: 11, padding: "2px 8px" }}>{skill}</Tag>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        marginTop: 12, 
                        paddingTop: 12, 
                        borderTop: "1px solid #f0f0f0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Monthly Budget</Text>
                        <Text strong style={{ fontSize: 16, color: "#014c75" }}>
                          {formatCurrency(job.monthlyBudgetINR)}
                        </Text>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: 60, color: "#8c8c8c" }}>
                  <TeamOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                  <div>No job recommendations available</div>
                </div>
              )}
            </div>

            <div style={modalStyles.footer}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Checkbox 
                  checked={selectedJobs.length === jobs.length && jobs.length > 0}
                  indeterminate={selectedJobs.length > 0 && selectedJobs.length < jobs.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedJobs(jobs.map((_, i) => i));
                    } else {
                      setSelectedJobs([]);
                    }
                  }}
                  disabled={jobs.length === 0}
                />
                <Text type="secondary">Select All ({selectedJobs.length}/{jobs.length} selected)</Text>
              </div>
              <Space size="middle">
                <Button size="large" onClick={() => setActionModalVisible(false)}>Cancel</Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  loading={creatingJobs}
                  onClick={handleCreateDraftJobs}
                  disabled={selectedJobs.length === 0 || !selectedEngagement || activeEngagements.length === 0}
                  style={{ background: "#01d9a9", borderColor: "#01d9a9", height: 44, paddingLeft: 24, paddingRight: 24 }}
                >
                  Create {selectedJobs.length} Draft Job(s)
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;
    
    setDownloading(true);
    message.loading({ content: "Generating PDF...", key: "pdf" });
    
    const wrapper = reportRef.current.closest('.report-wrapper-main');
    
    try {
      if (wrapper) wrapper.classList.add('pdf-mode');
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 1200
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const marginLeft = 12;
      const marginRight = 12;
      const marginTop = 20;
      const marginBottom = 20;
      const headerHeight = 10;
      const footerHeight = 10;
      
      const contentWidth = pageWidth - marginLeft - marginRight;
      const contentHeight = pageHeight - marginTop - marginBottom - headerHeight - footerHeight;
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const totalPages = Math.ceil(imgHeight / contentHeight);
      
      const addHeaderFooter = (pageNum) => {
        pdf.setFillColor(1, 76, 117);
        pdf.rect(0, 0, pageWidth, 12, 'F');
        
        pdf.setFillColor(1, 217, 169);
        pdf.rect(0, 12, pageWidth, 2, 'F');
        
        pdf.setFontSize(9);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.text("HireNXT", marginLeft, 8);
        
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text("AI Assessment Report", pageWidth - marginRight - 35, 8);
        
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.3);
        pdf.line(marginLeft, pageHeight - marginBottom + 2, pageWidth - marginRight, pageHeight - marginBottom + 2);
        
        pdf.setFontSize(8);
        pdf.setTextColor(120, 120, 120);
        pdf.setFont("helvetica", "normal");
        
        const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        pdf.text(`Generated: ${dateStr}`, marginLeft, pageHeight - 8);
        
        pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - marginRight - 20, pageHeight - 8);
        
        const centerText = assessment?.customerOrganization || "Assessment Report";
        const textWidth = pdf.getTextWidth(centerText);
        pdf.text(centerText, (pageWidth - textWidth) / 2, pageHeight - 8);
      };
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        addHeaderFooter(page + 1);
        
        const sourceY = page * contentHeight * (canvas.width / imgWidth);
        const sourceHeight = Math.min(contentHeight * (canvas.width / imgWidth), canvas.height - sourceY);
        
        if (sourceHeight <= 0) break;
        
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = sourceHeight;
        const ctx = tempCanvas.getContext('2d');
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        
        ctx.drawImage(
          canvas,
          0, sourceY,
          canvas.width, sourceHeight,
          0, 0,
          canvas.width, sourceHeight
        );
        
        const pageImgData = tempCanvas.toDataURL("image/png");
        const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;
        
        pdf.addImage(pageImgData, "PNG", marginLeft, marginTop + headerHeight, imgWidth, pageImgHeight);
      }
      
      const fileName = `Assessment_Report_${assessment?.customerOrganization?.replace(/\s+/g, "_") || "Report"}_${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);
      
      message.success({ content: "PDF downloaded successfully!", key: "pdf" });
    } catch (error) {
      console.error("PDF generation failed:", error);
      message.error({ content: "Failed to generate PDF. Please try again.", key: "pdf" });
    } finally {
      if (wrapper) wrapper.classList.remove('pdf-mode');
      setDownloading(false);
    }
  };

  if (!report) {
    return <div>No report available</div>;
  }

  const teamColumns = [
    { title: "Role", dataIndex: "role", key: "role" },
    { title: "Seniority", dataIndex: "seniority", key: "seniority" },
    { title: "Qty", dataIndex: "quantity", key: "quantity", width: 60 },
    { title: "Duration", dataIndex: "durationMonths", key: "duration", render: (v) => `${v} months` },
    { title: "Monthly Cost", dataIndex: "monthlyCostINR", key: "cost", render: (v) => formatCurrency(v) },
  ];

  const costColumns = [
    { title: "Model", dataIndex: "model", key: "model" },
    { title: "Monthly Cost", dataIndex: "monthlyCost", key: "monthly", render: (v) => formatCurrency(v) },
    { title: "Annual Cost", dataIndex: "annualCost", key: "annual", render: (v) => formatCurrency(v) },
    { title: "Flexibility", dataIndex: "flexibility", key: "flexibility", render: (v) => <Tag color={v === "High" ? "green" : "orange"}>{v}</Tag> },
    { title: "Risk", dataIndex: "risk", key: "risk", render: (v) => <Tag color={v === "Low" ? "green" : "red"}>{v}</Tag> },
  ];

  return (
    <ReportWrapper className="report-wrapper-main">
      <div className="report-container" ref={reportRef}>
        <div className="report-header">
          <div className="header-top">
            <div className="header-badge">AI-Powered Assessment Report</div>
            <div className="enterprise-tag">Enterprise Ready</div>
          </div>
          <h1>Technical Readiness & Resource Strategy Report</h1>
          <div className="header-subtitle">
            Comprehensive analysis and actionable insights for {assessment?.customerOrganization}
          </div>
          <div className="header-meta">
            <div className="meta-item">
              <div className="meta-icon"><CalendarOutlined /></div>
              Generated: {new Date(assessment?.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="meta-item">
              <div className="meta-icon"><TeamOutlined /></div>
              Team Recommendations: {report.resourceStrategy?.impactTeam?.length || 0} Roles
            </div>
            <div className="meta-item">
              <div className="meta-icon"><ThunderboltOutlined /></div>
              Maturity Index: {report.executiveSummary?.transformationMaturityIndex || 0}/100
            </div>
          </div>
          <Button 
            className="hide-in-pdf"
            icon={<FilePdfOutlined />} 
            onClick={handleDownloadPDF}
            loading={downloading}
            style={{ marginTop: 20, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "white", fontWeight: 600, height: 40, paddingLeft: 20, paddingRight: 20 }}
          >
            Download Report PDF
          </Button>
        </div>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#e6f7ff", color: "#1890ff" }}>
                <RocketOutlined />
              </span>
              Section 1: Executive Summary
            </span>
          }
        >
          <Paragraph>{report.executiveSummary?.projectSummary}</Paragraph>
          
          <div className="maturity-display">
            <div className="maturity-score">
              <div className="score-value">{report.executiveSummary?.transformationMaturityIndex || 0}</div>
              <div className="score-label">Maturity Index</div>
            </div>
            <div className="maturity-details">
              <div className={`stage-badge ${getMaturityStageClass(report.executiveSummary?.maturityStage)}`}>
                {report.executiveSummary?.maturityStage}
              </div>
              <Paragraph style={{ marginBottom: 0 }}>
                {report.executiveSummary?.keyRecommendation}
              </Paragraph>
            </div>
          </div>
        </Card>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#fff7e6", color: "#fa8c16" }}>
                <InfoCircleOutlined />
              </span>
              Section 2: Digital Readiness & Risk Profile
            </span>
          }
        >
          <div className="risk-grid">
            <div className="risk-item">
              <div className="risk-label">Technical Complexity</div>
              <div className={`risk-value ${getRiskClass(report.digitalReadiness?.technicalComplexity)}`}>
                {report.digitalReadiness?.technicalComplexity}
              </div>
            </div>
            <div className="risk-item">
              <div className="risk-label">Delivery Risk</div>
              <div className={`risk-value ${getRiskClass(report.digitalReadiness?.deliveryRisk)}`}>
                {report.digitalReadiness?.deliveryRisk}
              </div>
            </div>
            <div className="risk-item">
              <div className="risk-label">Change Readiness</div>
              <div className="risk-value" style={{ color: "#1890ff" }}>
                {report.digitalReadiness?.changeReadinessScore}/100
              </div>
            </div>
          </div>

          <Title level={5}>Key Risk Factors</Title>
          <ul className="risk-factors-list">
            {report.digitalReadiness?.keyRiskFactors?.map((risk, idx) => (
              <li key={idx}>
                <span className="risk-bullet">⚠️</span>
                {risk}
              </li>
            ))}
          </ul>
        </Card>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#f6ffed", color: "#52c41a" }}>
                <TeamOutlined />
              </span>
              Section 3: Resource Strategy
            </span>
          }
        >
          <div className="team-section">
            <div className="team-title">
              <RocketOutlined /> Recommended Team (Impact Team)
            </div>
            <Table
              columns={teamColumns}
              dataSource={report.resourceStrategy?.impactTeam?.map((t, i) => ({ ...t, key: i }))}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
            />
            <div className="team-total">
              <div className="total-label">Total Monthly Cost</div>
              <div className="total-value">{formatCurrency(report.resourceStrategy?.totalImpactTeamCost)}</div>
            </div>
          </div>

          <Divider />

          <div className="team-section">
            <div className="team-title">
              <TeamOutlined /> Minimum Viable Team (MVT)
            </div>
            <Table
              columns={teamColumns}
              dataSource={report.resourceStrategy?.minimumViableTeam?.map((t, i) => ({ ...t, key: i }))}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
            />
            <div className="team-total">
              <div className="total-label">Total Monthly Cost (MVT)</div>
              <div className="total-value">{formatCurrency(report.resourceStrategy?.totalMVTCost)}</div>
            </div>
          </div>
        </Card>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#fff0f6", color: "#eb2f96" }}>
                <DollarOutlined />
              </span>
              Section 4: Cost Strategy - Staff Augmentation vs Full-Time
            </span>
          }
        >
          <div className="cost-comparison">
            <Table
              columns={costColumns}
              dataSource={report.costStrategy?.comparison?.map((c, i) => ({ ...c, key: i }))}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              size="small"
            />
          </div>

          <div className="savings-highlight">
            <div className="savings-icon">
              <DollarOutlined />
            </div>
            <div className="savings-text">
              <div className="savings-percentage">{report.costStrategy?.savingsPercentage}% Cost Savings</div>
              <div className="savings-desc">
                {report.costStrategy?.cashFlowAdvantage}
              </div>
              <Text type="secondary">{report.costStrategy?.exitFlexibility}</Text>
            </div>
          </div>
        </Card>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#f0f5ff", color: "#2f54eb" }}>
                <CalendarOutlined />
              </span>
              Section 5: Hiring Roadmap
            </span>
          }
        >
          <div className="roadmap-timeline">
            {report.hiringRoadmap?.map((phase, idx) => (
              <div className="phase-item" key={idx}>
                <div className="phase-dot" />
                <div className="phase-header">
                  <span className="phase-name">{phase.phase}</span>
                  <span className="phase-timeline">{phase.timeline}</span>
                </div>
                <div className="phase-roles">
                  {phase.roles?.map((role, rIdx) => (
                    <Tag key={rIdx} color="blue">{role}</Tag>
                  ))}
                </div>
                <div className="phase-desc">{phase.description}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card
          className="section-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#e6fffb", color: "#13c2c2" }}>
                <FileTextOutlined />
              </span>
              Section 6: Recommended Job Roles
            </span>
          }
        >
          {report.jobDescriptions?.map((job, idx) => (
            <div className="job-card" key={idx}>
              <div className="job-header">
                <div className="job-title">{job.role}</div>
                <div className="job-budget">
                  <div className="budget-value">{formatCurrency(job.monthlyBudgetINR)}</div>
                  <div className="budget-label">per month</div>
                </div>
              </div>
              
              {job.whyRequired && (
                <div className="job-why">
                  <strong>Why Required:</strong> {job.whyRequired}
                </div>
              )}

              <div className="job-skills">
                {job.primarySkills?.map((skill, sIdx) => (
                  <Tag key={sIdx} className="skill-tag primary">{skill}</Tag>
                ))}
                {job.secondarySkills?.map((skill, sIdx) => (
                  <Tag key={`s-${sIdx}`} className="skill-tag secondary">{skill}</Tag>
                ))}
              </div>

              <div className="job-meta">
                <span className="meta-item">
                  <TeamOutlined /> {job.experienceRange}
                </span>
                {job.recommendedDurationMonths && (
                  <span className="meta-item">
                    <CalendarOutlined /> {job.recommendedDurationMonths} months
                  </span>
                )}
              </div>
            </div>
          ))}
        </Card>

        <Card
          className="section-card next-actions-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#fff1f0", color: "#f5222d" }}>
                <CheckCircleOutlined />
              </span>
              Section 7: Recommended Next Steps
            </span>
          }
        >
          <div className="actions-list">
            {report.nextBestActions?.map((action, idx) => (
              <div className="action-item" key={idx}>
                <span className="action-number">{idx + 1}</span>
                <span className="action-text">{action}</span>
                <ArrowRightOutlined className="action-arrow hide-in-pdf" />
              </div>
            ))}
          </div>
        </Card>

        <Card
          className="section-card action-card"
          title={
            <span>
              <span className="section-icon" style={{ background: "#f0f5ff", color: "#2f54eb" }}>
                <ArrowRightOutlined />
              </span>
              Take Action
            </span>
          }
        >
          <div className="action-buttons-grid">
            <div className="action-button-card" onClick={() => handleOpenActionModal("jobs")}>
              <div className="action-icon" style={{ background: "#e6f7ff", color: "#1890ff" }}>
                <PlusOutlined />
              </div>
              <div className="action-content">
                <div className="action-title">Create Job Postings</div>
                <div className="action-description">
                  Create job postings based on the recommended roles from this assessment
                </div>
              </div>
              <ArrowRightOutlined className="action-arrow-icon" />
            </div>

            <div className="action-button-card" onClick={() => handleOpenActionModal("project")}>
              <div className="action-icon" style={{ background: "#f9f0ff", color: "#722ed1" }}>
                <FolderAddOutlined />
              </div>
              <div className="action-content">
                <div className="action-title">Create Project</div>
                <div className="action-description">
                  Convert this assessment into a full project with team and budget planning
                </div>
              </div>
              <ArrowRightOutlined className="action-arrow-icon" />
            </div>

            {onViewAssessments && (
              <div className="action-button-card" onClick={onViewAssessments}>
                <div className="action-icon" style={{ background: "#f6ffed", color: "#52c41a" }}>
                  <EyeOutlined />
                </div>
                <div className="action-content">
                  <div className="action-title">View All Assessments</div>
                  <div className="action-description">
                    Browse and compare all your project assessments in one place
                  </div>
                </div>
                <ArrowRightOutlined className="action-arrow-icon" />
              </div>
            )}

            <div className="action-button-card" onClick={handleDownloadPDF}>
              <div className="action-icon" style={{ background: "#fff1f0", color: "#f5222d" }}>
                <FilePdfOutlined />
              </div>
              <div className="action-content">
                <div className="action-title">Download Report</div>
                <div className="action-description">
                  Save this assessment report as a PDF for offline reference
                </div>
              </div>
              <ArrowRightOutlined className="action-arrow-icon" />
            </div>
          </div>
        </Card>

        <div className="data-credibility-footer">
          <div className="credibility-icon">
            <CheckCircleOutlined />
          </div>
          <div className="credibility-content">
            <div className="credibility-title">
              Data-Driven Insights
              <span className="verified-badge">Verified</span>
            </div>
            <div className="credibility-text">
              All data, benchmarks, and industry insights in this report are gathered from Gartner, S&P Global, Business Insider, Moneycontrol, Naukri, and several other legitimate sources. The data is credible and regularly updated to ensure accuracy.
            </div>
            <div className="source-logos">
              <span className="source-item">Gartner</span>
              <span className="source-item">S&P Global</span>
              <span className="source-item">Business Insider</span>
              <span className="source-item">Moneycontrol</span>
              <span className="source-item">Naukri</span>
            </div>
          </div>
        </div>

        <div className="report-actions">
          <Button size="large" onClick={onBack}>
            Back to Projects
          </Button>
          <Space>
            <Button size="large" icon={<FolderAddOutlined />} onClick={() => handleOpenActionModal("project")}>
              Create Project
            </Button>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => handleOpenActionModal("jobs")} style={{ background: "#01d9a9", borderColor: "#01d9a9" }}>
              Create Jobs
            </Button>
          </Space>
        </div>
      </div>
      {renderActionModal()}
    </ReportWrapper>
  );
};

export default AdminAssessmentReportDisplay;
